import { createClient } from "@supabase/supabase-js";

/* Founding-100 waitlist capture for the marketing page (index.html). Until
   the Stripe rail ships (monetization plan Phase 2), the Premium/Lifetime
   pricing cards collect an email instead of linking a "Buy" button that would
   charge nobody and grant nothing.

   No dependency on the app's Supabase client (src/account/supabaseClient.js)
   — that one persists an auth session; this is a fire-and-forget anon RPC
   call from a page that never signs anyone in, so it gets its own minimal,
   session-less client.

   Markup contract, so the landing page can just declare these attributes:
     <form data-waitlist-form>
       <input type="email" required />
       <button type="submit">…</button>
       <span data-waitlist-status></span>   (optional — success/error text)
     </form>
     <span data-waitlist-count></span>       (optional — live "N of 100" line)
   locale/region come from the page's own lang-chip / region-select state via
   localStorage (forma-landing-lang / forma-landing-region), already written
   by index.html's inline script — read fresh at submit time so this module
   doesn't need to know about that script's internals. */

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = url && anonKey
  ? createClient(url, anonKey, { auth: { persistSession: false } })
  : null;

const STATUS_TEXT = {
  en: { ok: "You're on the list — we'll email you when Premium is live.", err: "Something went wrong — try again, or email hello@zoldanlabs.com.", invalid: "Enter a valid email." },
  pt: { ok: "Você está na lista — avisamos por e-mail quando o Premium estiver disponível.", err: "Algo deu errado — tente de novo, ou escreva para hello@zoldanlabs.com.", invalid: "Digite um e-mail válido." },
  es: { ok: "Estás en la lista — te avisamos por correo cuando Premium esté disponible.", err: "Algo salió mal — inténtalo de nuevo, o escribe a hello@zoldanlabs.com.", invalid: "Escribe un correo válido." },
};
const currentLang = () => {
  try { return localStorage.getItem("forma-landing-lang") || "en"; } catch { return "en"; }
};
const currentRegion = () => {
  try { return localStorage.getItem("forma-landing-region") || "US"; } catch { return "US"; }
};

async function refreshCount() {
  if (!supabase) return;
  const { data, error } = await supabase.rpc("waitlist_count");
  if (error) return;
  document.querySelectorAll("[data-waitlist-count]").forEach((el) => { el.textContent = String(data); });
}

function wireForm(form) {
  const status = form.querySelector("[data-waitlist-status]");
  const setStatus = (text, ok) => {
    if (!status) return;
    status.textContent = text;
    status.style.color = ok ? "var(--green-hi)" : "var(--garnet)";
  };
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const input = form.querySelector('input[type="email"]');
    const email = (input?.value || "").trim();
    const t = STATUS_TEXT[currentLang()] || STATUS_TEXT.en;
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setStatus(t.invalid, false); return; }
    if (!supabase) { setStatus(t.err, false); return; }
    const btn = form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;
    const { error } = await supabase.rpc("join_waitlist", { p_email: email, p_locale: currentLang(), p_region: currentRegion() });
    if (btn) btn.disabled = false;
    if (error) { setStatus(t.err, false); return; }
    setStatus(t.ok, true);
    form.reset();
    window.plausible?.("waitlist_joined");
    refreshCount();
  });
}

document.querySelectorAll("[data-waitlist-form]").forEach(wireForm);
refreshCount();
